/**
 * AbilityTestArena — Dedicated test map for ability verification.
 *
 * Features:
 * - Flat ground, dark background (high contrast for VFX)
 * - 5 low-health arena pigs at fixed positions for visible impact/AoE checks
 * - Player spawns left side facing the targets
 * - No wandering mobs, no parallax, minimal clutter
 *
 * Used by: E2E ability video tests, manual QA, ability designer preview
 */

import { Platform } from '../../shared/CharacterTypes';
import { MAP_DEFAULTS } from '../../config/map/defaults.config';
import { GridLevel, LayerType } from '../../shared/TileEditorTypes';
import { W, H, T } from './StarterMap';

const MAP_ID = 'ability-test-arena';
const MAP_NAME = 'Ability Test Arena';
const VERSION = '1.0';

// Empty editor layers
const groundLayer = new Map<string, number>();
const collisionLayer = new Map<string, number>();
const objectsLayer = new Map<string, number>();
const entitiesLayer = new Map<string, number>();
const triggersLayer = new Map<string, number>();

// Ground position
export const ARENA_GROUND_Y = 550;
export const ARENA_SPAWN_X = 200;
// Player spawn — player is exactly 40×40, so 550 - 40 = 510 is correct.
// Do NOT generalize this -40 offset to any other entity type.
export const ARENA_SPAWN_Y = 510;

// pig hitbox — must match entities.gen.ts pig.height (60×50). entity.y is the
// TOP of the hitbox; see OneVersusOnePigMap.ts:28-32 for the convention
// citation. Pigs replaced training_dummies as ability-QA targets — same
// hitbox size + position math, but visually live (sprite + idle anim).
// HP is overridden to 100 via properties so they actually die during QA
// (plain pig is 30 HP, dies to a single mage_auto). Respawn cycle uses
// the global MOB_AI defaults (deathDespawnMs:3000 + respawnMs:10000 =
// ~13 sec from kill to back-in-arena) — fine for first-pass ability VQA.
const PIG_HEIGHT = 50;

export const ARENA_DUMMY_POSITIONS = [
  { x: 450,  y: ARENA_GROUND_Y - PIG_HEIGHT },
  { x: 650,  y: ARENA_GROUND_Y - PIG_HEIGHT },
  { x: 850,  y: ARENA_GROUND_Y - PIG_HEIGHT },
  { x: 1050, y: ARENA_GROUND_Y - PIG_HEIGHT },
  { x: 1250, y: ARENA_GROUND_Y - PIG_HEIGHT },
];

// Map entities — these are read by EntitySpawnerService
const entities: any[] = ARENA_DUMMY_POSITIONS.map((pos, i) => ({
  type: 'pig',
  id: `arena_pig_${i + 1}`,
  x: pos.x,
  y: pos.y,
  properties: { displayName: `Arena Pig ${i + 1}`, hp: 100 },
}));

const spawnPoints = [
  { x: ARENA_SPAWN_X / T, y: ARENA_SPAWN_Y / T }
];

const defaultSpawn = {
  x: ARENA_SPAWN_X,
  y: ARENA_SPAWN_Y,
};

const runtimePlatforms: Platform[] = [
  // Main ground — full width
  {
    id: 'arena-ground',
    x: 0,
    y: ARENA_GROUND_Y,
    width: W * T,
    height: 40,
    type: 'normal' as const,
    color: '#2d3748',
  },
  // Raised platform for testing vertical abilities
  {
    id: 'arena-platform-upper',
    x: 600,
    y: ARENA_GROUND_Y - 150,
    width: 300,
    height: 20,
    type: 'normal' as const,
    color: '#4a5568',
    isOneWay: true,
  },
];

export const AbilityTestArena: GridLevel & Partial<any> = {
  id: MAP_ID,
  name: MAP_NAME,
  version: VERSION,
  widthTiles: W,
  heightTiles: H,
  width: W * T,
  height: H * T,
  tileSize: T,
  backgroundColor: '#1a1a2e',
  gravity: MAP_DEFAULTS.gravity,

  spawnPoints,
  defaultSpawn,

  layers: [
    { name: LayerType.GROUND, visible: true, locked: false, opacity: 1.0, tiles: groundLayer },
    { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: collisionLayer },
    { name: LayerType.OBJECTS, visible: true, locked: false, opacity: 1.0, tiles: objectsLayer },
    { name: LayerType.ENTITIES, visible: true, locked: false, opacity: 1.0, tiles: entitiesLayer, entities },
    { name: LayerType.TRIGGERS, visible: true, locked: false, opacity: 1.0, tiles: triggersLayer },
  ],

  platforms: runtimePlatforms,
  spawnRules: [],
  type: 'field',
  metadata: {
    author: 'Helix2000 System',
    description: 'Isolated arena with 5 low-health pigs for ability testing, VFX verification, and E2E video capture.',
    difficulty: 'Low',
    maxPlayers: 1,
    version: VERSION,
    tags: ['testing', 'arena', 'abilities', 'pigs'],
    // Map rule profile — `lobby_safe` template + AOE-PvP override.
    // PvP is on so testing AoE friendly-fire patterns is possible,
    // but bots stay at 'ignore' (arena targets are sparring partners,
    // not aggressors). Flip botBehavior via /pvp slash command to
    // exercise bot AoE.
    profile: 'lobby_safe',
    overrides: {
      pvp: { enabled: true, botBehavior: 'ignore' },
    },
  },
};
